import { GlobalService } from './global.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { catchError, firstValueFrom, Observable, throwError } from 'rxjs';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import {
  AccessGroupClass,
  AccessEntryClass,
  AccessPermissionClass,
  UsersAccessPermissionClass,
  UsersAccessManagerClass,
  AccessCustomerClass,
  AccessPolicyClass,
} from '../models/acess-class';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  constructor(
    private http: HttpClient,
    public globalService: GlobalService,
  ) {}

  getGroups() {
    return this.http.get<AccessGroupClass[]>(`${environment.URL}/access/GetGroups`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getEntriesByGroup(idGroup: number | null) {
    const groupId = idGroup ?? 0;
    return this.http.get<AccessEntryClass[]>(`${environment.URL}/access/GetEntriesByGroup/${groupId}`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getAccessibleEntries() {
    return this.http.get<AccessEntryClass[]>(`${environment.URL}/access/GetAccessibleEntries`).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setGroup(group: AccessGroupClass) {
    return this.http.post(`${environment.URL}/access/SetGroup`, group).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setEntry(entry: AccessEntryClass) {
    return this.http.post(`${environment.URL}/access/SetEntry`, entry).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  setPermission(permission: AccessPermissionClass) {
    return this.http.post(`${environment.URL}/access/SetPermission`, permission).pipe(
      catchError(error => {
        const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  private accessValidationCache = new Map<string, { authorized: boolean; ts: number }>();

  async GetUsersAccessValidate(module: string, feature: string, forceLive = false): Promise<boolean> {
    if (!this.globalService.loggedUserClass?.idUser) {
      console.warn('[GetUsersAccessValidate] User not logged in, access denied');
      return false;
    }
    const idUser = this.globalService.loggedUserClass.idUser;
    const cacheKey = `${idUser}:${module}:${feature}`;
    const cached = !forceLive ? this.accessValidationCache.get(cacheKey) : undefined;
    const now = Date.now();

    const fetchAccess = async (): Promise<boolean> => {
      const result = await firstValueFrom(
        this.http.post<any[]>(`${environment.URL}/access/GetUsersAccessValidate`, {
          module,
          feature,
          inidUserLogged: idUser,
        }),
      );
      const authorized = Number(result?.[0]?.authorized) === 1;
      this.accessValidationCache.set(cacheKey, { authorized, ts: Date.now() });
      return authorized;
    };

    if (!forceLive && cached && now - cached.ts < 10 * 60 * 1000) {
      if (now - cached.ts > 60 * 1000) {
        fetchAccess().catch(() => {});
      }
      return cached.authorized;
    }

    try {
      return await fetchAccess();
    } catch (error: any) {
      console.warn('[GetUsersAccessValidate] Erro ou acesso negado:', error);

      // Propagar o erro se for falha de rede (0) ou servidor (5xx)
      if (error && (error.status === 0 || error.status >= 500)) {
        throw error;
      }

      // Garante que qualquer falha devolve "não autorizado"
      return false;
    }
  }

  clearAccessValidationCache(): void {
    this.accessValidationCache.clear();
  }

  async GetUsersAccessPermissionsFull(filters: {
    name?: string;
    nif?: string;
    actived?: boolean | number | null;
    admin?: boolean | number | null;
    module?: string | null;
    feature?: string | null;
    description?: string | null;
    authorized?: boolean | number | null;
  }): Promise<UsersAccessManagerClass[]> {
    const idUserLogged = this.globalService.loggedUserClass.idUser;

    const body = {
      inidUserLogged: idUserLogged.toString(),
      name: filters.name,
      nif: filters.nif,
      actived: filters.actived !== undefined && filters.actived !== null ? String(filters.actived) : undefined,
      admin: filters.admin !== undefined && filters.admin !== null ? String(filters.admin) : undefined,
      module: filters.module,
      feature: filters.feature,
      description: filters.description,
      authorized:
        filters.authorized !== undefined && filters.authorized !== null ? String(filters.authorized) : undefined,
    };

    try {
      const result = await firstValueFrom(
        this.http.post<UsersAccessManagerClass[]>(`${environment.URL}/access/GetUsersAccessPermissionsFull`, body),
      );
      return result || [];
    } catch (error) {
      const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
      return Promise.reject(errorMessage);
    }
  }

  async SetUsersAccessPermission(rule: UsersAccessPermissionClass): Promise<void> {
    rule.UUIDLastChange = this.globalService.loggedUserClass.UUID;

    const payload = {
      inidUser: rule.idUser,
      inidUsersAccessRules: rule.idUsersAccessRules,
      inAuthorized: rule.Authorized,
      inDateStart: rule.DateStart || null,
      inDateEnd: rule.DateEnd || null,
      inUUID: rule.UUID,
      inUUIDLastChange: rule.UUIDLastChange,
    };

    try {
      await firstValueFrom(this.http.post(`${environment.URL}/access/SetUsersAccessPermission`, payload));
      this.accessValidationCache.clear();
    } catch (error) {
      const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
      return Promise.reject(errorMessage);
    }
  }

  UsersAccessValidateCustomerIdsWithAccess(idUser: number): Observable<AccessCustomerClass[]> {
    return this.http
      .get<AccessCustomerClass[]>(`${environment.URL}/access/UsersAccessValidateCustomerIdsWithAccess/${idUser}`)
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  UsersAccessValidateInsurancePolicyIdsWithAccess(idUser: number, idCustomer: number): Observable<AccessPolicyClass[]> {
    return this.http
      .get<
        AccessPolicyClass[]
      >(`${environment.URL}/access/UsersAccessValidateInsurancePolicyIdsWithAccess/${idUser}/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  async GetUsersAreasFlags(): Promise<any[]> {
    try {
      const result = await firstValueFrom(this.http.get<any[]>(`${environment.URL}/access/GetUsersAreasFlags`));
      return result || [];
    } catch (error) {
      console.warn('[GetUsersAreasFlags] Erro:', error);
      return [];
    }
  }

  async SetUserAreaFlag(idUser: number, field: string, value: any): Promise<any> {
    try {
      const res = await firstValueFrom(
        this.http.post(`${environment.URL}/access/SetUserAreaFlag`, { idUser, field, value }),
      );
      this.accessValidationCache.clear();
      return res;
    } catch (error) {
      const errorMessage = HttpclienterrorService.getServerErrorMessage(error);
      return Promise.reject(errorMessage);
    }
  }
}
